// Utils
import type { ComparePeriodValues } from "src/components/ComparePeriodSelector/ComparePeriodSelector.logic";
import type { TFunction } from "src/design-system/core/TranslationContext";
import {
    createStaticCompareRanges,
    createStaticRanges,
    currentFullYearRange,
    currentMonthRange,
    currentMonthRangeEndToday,
    currentWeekRange,
    currentWeekRangeEndToday,
    currentYearRange,
    last12MonthsRange,
    last15DaysRangeAndToday,
    last30DaysRange,
    last30DaysRangeAndToday,
    last3MonthsRange,
    last7DaysRange,
    lastFourWeeksCompare,
    lastMonthRange,
    lastTwoWeeksCompare,
    lastTwoWeeksRange,
    lastWeekCompare,
    lastWeekRange,
    monthLastYearCompare,
    nextDayRange,
    nextWeekRange,
    todayRange,
    weekLastYearCompare,
    yesterdayRange,
} from "src/utils/timeRanges";

// Types
import { isNil } from "lodash";
import { CompareRangeType } from "src/types/enum/CompareRangeType";
import { DevelopmentEnum } from "src/types/enum/DevelopmentEnum";
import { RangeType } from "src/types/enum/RangeType";
import type { PartialStaticRange, StaticCompareRange, StaticRange } from "src/types/staticRange";
import { ComparisonMode, ComparisonType } from "src/views-ds/dashboard/types";

// The ranges here are in a specific order to be displayed in the UI, so we need to keep this order in the getRanges function and not sort them
export const getRanges = (isFuture = false): PartialStaticRange<RangeType>[] => {
    const baseRanges: PartialStaticRange<RangeType>[] = [
        {
            key: RangeType.lastTwelveMonths,
            range: (): typeof last12MonthsRange => last12MonthsRange,
        },
        {
            key: RangeType.currentYear,
            range: isFuture
                ? (): typeof currentFullYearRange => currentFullYearRange
                : (): typeof currentYearRange => currentYearRange,
        },
        {
            key: RangeType.lastMonth,
            range: (): typeof lastMonthRange => lastMonthRange,
        },
        {
            key: RangeType.currentMonth,
            range: isFuture
                ? (): typeof currentMonthRange => currentMonthRange
                : (): typeof currentMonthRangeEndToday => currentMonthRangeEndToday,
        },
        {
            key: RangeType.last30DaysAndToday,
            range: (): typeof last30DaysRangeAndToday => last30DaysRangeAndToday,
        },
        {
            key: RangeType.last15DaysAndToday,
            range: (): typeof last15DaysRangeAndToday => last15DaysRangeAndToday,
        },
        {
            key: RangeType.lastWeek,
            range: (): typeof lastWeekRange => lastWeekRange,
        },
        {
            key: RangeType.onGoingWeek,
            range: isFuture
                ? (): typeof currentWeekRange => currentWeekRange
                : (): typeof currentWeekRangeEndToday => currentWeekRangeEndToday,
        },
    ];

    if (isFuture) {
        baseRanges.push({
            key: RangeType.nextWeek,
            range: (): typeof nextWeekRange => nextWeekRange,
        });
    }

    baseRanges.push(
        {
            key: RangeType.yesterday,
            range: (): typeof yesterdayRange => yesterdayRange,
        },
        {
            key: RangeType.today,
            range: (): typeof todayRange => todayRange,
        }
    );

    if (isFuture) {
        baseRanges.push({
            key: RangeType.tomorrow,
            range: (): typeof nextDayRange => nextDayRange,
        });
    }

    return baseRanges;
};

export const ranges: PartialStaticRange<RangeType>[] = getRanges();

export const rangesWithFutures: PartialStaticRange<RangeType>[] = getRanges(true);

// Kind of a band aid because of the harmonization and the fact some ranges are duplicated and old ones may be saved in the local storage settings
const equivalentRangesMapping = {
    [RangeType.currentWeek]: RangeType.onGoingWeek,
    [RangeType.onGoingWeek]: RangeType.onGoingWeek,
    [RangeType.currentMonth]: RangeType.currentMonth,
    [RangeType.onGoingMonth]: RangeType.currentMonth,
    [RangeType.currentDay]: RangeType.today,
    [RangeType.today]: RangeType.today,
};

export function getValidRangeKey({
    staticRanges,
    key,
    defaultValue,
}: {
    staticRanges: StaticRange[];
    key: RangeType;
    defaultValue: RangeType;
}): RangeType {
    if (staticRanges.find((range) => range.key === key)) {
        return key;
    }
    return equivalentRangesMapping[key] || defaultValue;
}

const prodRangesHourlyMetrics: PartialStaticRange<RangeType>[] = [
    {
        key: RangeType.lastTwelveMonths,
        range: (): typeof last12MonthsRange => last12MonthsRange,
    },
    {
        key: RangeType.lastThreeMonths,
        range: (): typeof last3MonthsRange => last3MonthsRange,
    },
    {
        key: RangeType.lastThirtyDays,
        range: (): typeof last30DaysRange => last30DaysRange,
    },
    {
        key: RangeType.lastSevenDays,
        range: (): typeof last7DaysRange => last7DaysRange,
    },
];

const devRangesHourlyMetrics: PartialStaticRange<RangeType>[] = [
    {
        key: RangeType.lastTwoWeeks,
        range: (): typeof lastTwoWeeksRange => lastTwoWeeksRange,
    },
];

const rangesHourlyMetrics: PartialStaticRange<RangeType>[] = [
    ...prodRangesHourlyMetrics,
    ...(Object.values(DevelopmentEnum).includes(import.meta.env.VITE_FLYNT_ENV as DevelopmentEnum)
        ? devRangesHourlyMetrics
        : []),
];

export const rangePlanning: PartialStaticRange<RangeType>[] = [
    {
        key: RangeType.last15DaysAndToday,
        range: (): typeof last15DaysRangeAndToday => last15DaysRangeAndToday,
    },
    {
        key: RangeType.lastWeek,
        range: (): typeof lastWeekRange => lastWeekRange,
    },
    {
        key: RangeType.currentWeek,
        range: (): typeof currentWeekRange => currentWeekRange,
    },
    {
        key: RangeType.nextWeek,
        range: (): typeof nextWeekRange => nextWeekRange,
    },
    {
        key: RangeType.yesterday,
        range: (): typeof yesterdayRange => yesterdayRange,
    },
    {
        key: RangeType.today,
        range: (): typeof todayRange => todayRange,
    },
    {
        key: RangeType.tomorrow,
        range: (): typeof nextDayRange => nextDayRange,
    },
];

export const rangesAutoRulesTrigger: PartialStaticRange<CompareRangeType>[] = [
    {
        key: CompareRangeType.lastWeek,
        range: (): typeof lastWeekCompare => lastWeekCompare,
    },
    {
        key: CompareRangeType.lastTwoWeeks,
        range: (): typeof lastTwoWeeksCompare => lastTwoWeeksCompare,
    },
    {
        key: CompareRangeType.lastFourWeeks,
        range: (): typeof lastFourWeeksCompare => lastFourWeeksCompare,
    },
    {
        key: CompareRangeType.weekLastYearTantamount,
        range: (): typeof weekLastYearCompare => weekLastYearCompare,
    },
    {
        key: CompareRangeType.monthLastYearDateToDate,
        range: (): typeof monthLastYearCompare => monthLastYearCompare,
    },
];

export const rankingComparePeriodKeys: ComparePeriodValues<string[]> = {
    [RangeType.lastTwelveMonths]: [CompareRangeType.twelvePreviousMonthsTantamount],
    [RangeType.last30DaysAndToday]: [
        CompareRangeType.thirtyPreviousDaysDateToDate,
        CompareRangeType.sameDaysLastYearDateToDate,
    ],
    [RangeType.last15DaysAndToday]: [
        CompareRangeType.fifteenPreviousDaysDateToDate,
        CompareRangeType.sameDaysLastYearDateToDate,
    ],
    [RangeType.currentWeek]: [CompareRangeType.lastWeek],
    [RangeType.onGoingWeek]: [CompareRangeType.lastWeek],
    [RangeType.lastWeek]: [CompareRangeType.previousWeek],
    [RangeType.today]: [CompareRangeType.yesterday],
    [RangeType.yesterday]: [CompareRangeType.previousDay],
    [RangeType.currentMonth]: [CompareRangeType.previousMonthSameDayDateToDate],
    [RangeType.lastMonth]: [CompareRangeType.previousMonthDateToDate],
    [RangeType.currentYear]: [CompareRangeType.lastYearDateToDate],
    [RangeType.selection]: [CompareRangeType.selectionTantamount],
    [RangeType.currentDay]: [CompareRangeType.yesterday],
};

export const getDefaultStaticRangesPerformance = (transFn: TFunction): StaticRange[] =>
    createStaticRanges(ranges, transFn);

export const getDefaultStaticRangesManagement = (transFn: TFunction): StaticRange[] =>
    createStaticRanges(rangesWithFutures, transFn);

export const getDefaultStaticRangesHourlyMetrics = (transFn: TFunction): StaticRange[] =>
    createStaticRanges(rangesHourlyMetrics, transFn);

export const getDefaultStaticRangesPlanning = (transFn: TFunction): StaticRange[] =>
    createStaticRanges(rangePlanning, transFn);

export const getDefaultStaticRangesRanking = (transFn: TFunction): StaticRange[] =>
    createStaticRanges(ranges, transFn);

export const getDefaultStaticCompareRangesHeatMap = (transFn: TFunction): StaticCompareRange[] =>
    createStaticCompareRanges(rangesAutoRulesTrigger, transFn);

export const getDefaultStaticRangesDashBoard = (transFn: TFunction): StaticRange[] =>
    createStaticRanges(ranges, transFn);

export const rangesWelcomePage: PartialStaticRange<RangeType>[] = [
    {
        key: RangeType.lastTwelveMonths,
        range: (): typeof last12MonthsRange => last12MonthsRange,
    },
    {
        key: RangeType.currentYear,
        range: (): typeof currentYearRange => currentYearRange,
    },
    {
        key: RangeType.lastMonth,
        range: (): typeof lastMonthRange => lastMonthRange,
    },
    {
        key: RangeType.currentMonth,
        range: (): typeof currentMonthRangeEndToday => currentMonthRangeEndToday,
    },
    {
        key: RangeType.last30DaysAndToday,
        range: (): typeof last30DaysRangeAndToday => last30DaysRangeAndToday,
    },
    {
        key: RangeType.last15DaysAndToday,
        range: (): typeof last15DaysRangeAndToday => last15DaysRangeAndToday,
    },

    {
        key: RangeType.lastWeek,
        range: (): typeof lastWeekRange => lastWeekRange,
    },
];

export const getDefaultStaticRangesWelcomePage = (transFn: TFunction): StaticRange[] =>
    createStaticRanges(rangesWelcomePage, transFn);

export const findDefaultComparePeriodByRangeModeAndType = (
    range: RangeType,
    mode: ComparisonMode,
    type: ComparisonType
): CompareRangeType => {
    const comparePeriod = dashboardComparePeriodKeys[range][mode].find(
        (comparePeriod) => comparePeriod.default === type
    );

    if (isNil(comparePeriod)) {
        return dashboardComparePeriodKeys[range][mode].find(
            (comparePeriod) => comparePeriod.default === ComparisonType.previousPeriod
        )?.range;
    }

    return comparePeriod?.range;
};

// Les valeurs pour currentWeek/onGoingWeek ; currentDay/today et onGoingMonth§/CurrentMonth sont dupliquées parce que l'usage du Range de react-date-range retourne des valeurs différentes de nos valeurs habituelles
export const dashboardComparePeriodKeys: Partial<
    Record<
        RangeType,
        Record<
            ComparisonMode.dateToDate | ComparisonMode.tantamount,
            { range: CompareRangeType; default?: ComparisonType }[]
        >
    >
> = {
    [RangeType.lastTwelveMonths]: {
        [ComparisonMode.dateToDate]: [
            {
                range: CompareRangeType.twelvePreviousMonthsDateToDate,
                default: ComparisonType.previousPeriod,
            },
            // { range: CompareRangeType.monthLastYearDateToDate, default: ComparisonType.previousYear },
            { range: CompareRangeType.forceSelection },
        ],
        [ComparisonMode.tantamount]: [
            {
                range: CompareRangeType.twelvePreviousMonthsTantamount,
                default: ComparisonType.previousPeriod,
            },
            // { range: CompareRangeType.monthLastYearTantamount, default: ComparisonType.previousYear },
            { range: CompareRangeType.forceSelection },
        ],
    },
    [RangeType.currentYear]: {
        [ComparisonMode.dateToDate]: [
            { range: CompareRangeType.lastYearDateToDate, default: ComparisonType.previousPeriod },
            { range: CompareRangeType.forceSelection },
        ],
        [ComparisonMode.tantamount]: [
            { range: CompareRangeType.lastYearTantamount, default: ComparisonType.previousPeriod },
            { range: CompareRangeType.forceSelection },
        ],
    },
    [RangeType.lastMonth]: {
        [ComparisonMode.dateToDate]: [
            {
                range: CompareRangeType.previousMonthDateToDate,
                default: ComparisonType.previousPeriod,
            },
            {
                range: CompareRangeType.threePreviousMonthsDateToDate,
                default: ComparisonType.meanOfPeriod,
            },
            {
                range: CompareRangeType.monthLastYearDateToDate,
                default: ComparisonType.previousYear,
            },
            { range: CompareRangeType.forceSelection },
        ],
        [ComparisonMode.tantamount]: [
            {
                range: CompareRangeType.previousMonthTantamount,
                default: ComparisonType.previousPeriod,
            },
            {
                range: CompareRangeType.threePreviousMonthsTantamount,
                default: ComparisonType.meanOfPeriod,
            },
            {
                range: CompareRangeType.monthLastYearTantamount,
                default: ComparisonType.previousYear,
            },
            { range: CompareRangeType.forceSelection },
        ],
    },
    [RangeType.last30DaysAndToday]: {
        [ComparisonMode.dateToDate]: [
            {
                range: CompareRangeType.thirtyPreviousDaysDateToDate,
                default: ComparisonType.previousPeriod,
            },
            {
                range: CompareRangeType.sameDaysLastYearDateToDate,
                default: ComparisonType.previousYear,
            },
            { range: CompareRangeType.forceSelection },
        ],
        [ComparisonMode.tantamount]: [
            {
                range: CompareRangeType.thirtyPreviousDaysTantamount,
                default: ComparisonType.previousPeriod,
            },
            {
                range: CompareRangeType.sameDaysLastYearTantamount,
                default: ComparisonType.previousYear,
            },
            { range: CompareRangeType.forceSelection },
        ],
    },
    [RangeType.onGoingMonth]: {
        [ComparisonMode.dateToDate]: [
            {
                range: CompareRangeType.previousMonthSameDayDateToDate,
                default: ComparisonType.previousPeriod,
            },
            {
                range: CompareRangeType.threePreviousMonthsDateToDate,
                default: ComparisonType.meanOfPeriod,
            },
            {
                range: CompareRangeType.monthLastYearDateToDate,
                default: ComparisonType.previousYear,
            },
            { range: CompareRangeType.forceSelection },
        ],
        [ComparisonMode.tantamount]: [
            {
                range: CompareRangeType.previousMonthTantamount,
                default: ComparisonType.previousPeriod,
            },
            {
                range: CompareRangeType.threePreviousMonthsTantamount,
                default: ComparisonType.meanOfPeriod,
            },
            {
                range: CompareRangeType.monthLastYearTantamount,
                default: ComparisonType.previousYear,
            },
            { range: CompareRangeType.forceSelection },
        ],
    },
    [RangeType.currentMonth]: {
        [ComparisonMode.dateToDate]: [
            {
                range: CompareRangeType.previousMonthSameDayDateToDate,
                default: ComparisonType.previousPeriod,
            },
            {
                range: CompareRangeType.threePreviousMonthsDateToDate,
                default: ComparisonType.meanOfPeriod,
            },
            {
                range: CompareRangeType.monthLastYearDateToDate,
                default: ComparisonType.previousYear,
            },
            { range: CompareRangeType.forceSelection },
        ],
        [ComparisonMode.tantamount]: [
            {
                range: CompareRangeType.previousMonthTantamount,
                default: ComparisonType.previousPeriod,
            },
            {
                range: CompareRangeType.threePreviousMonthsTantamount,
                default: ComparisonType.meanOfPeriod,
            },
            {
                range: CompareRangeType.monthLastYearTantamount,
                default: ComparisonType.previousYear,
            },
            { range: CompareRangeType.forceSelection },
        ],
    },
    [RangeType.last15DaysAndToday]: {
        [ComparisonMode.dateToDate]: [
            {
                range: CompareRangeType.fifteenPreviousDaysDateToDate,
                default: ComparisonType.previousPeriod,
            },
            {
                range: CompareRangeType.sameDaysLastYearDateToDate,
                default: ComparisonType.previousYear,
            },
            { range: CompareRangeType.forceSelection },
        ],
        [ComparisonMode.tantamount]: [
            {
                range: CompareRangeType.fifteenPreviousDaysTantamount,
                default: ComparisonType.previousPeriod,
            },
            {
                range: CompareRangeType.sameDaysLastYearTantamount,
                default: ComparisonType.previousYear,
            },
            { range: CompareRangeType.forceSelection },
        ],
    },
    [RangeType.lastWeek]: {
        [ComparisonMode.dateToDate]: [
            { range: CompareRangeType.previousWeek, default: ComparisonType.previousPeriod },
            { range: CompareRangeType.lastFourWeeks, default: ComparisonType.meanOfPeriod },
            {
                range: CompareRangeType.weekLastYearDateToDate,
                default: ComparisonType.previousYear,
            },
            { range: CompareRangeType.forceSelection },
        ],
        [ComparisonMode.tantamount]: [
            { range: CompareRangeType.previousWeek, default: ComparisonType.previousPeriod },
            { range: CompareRangeType.lastFourWeeks, default: ComparisonType.meanOfPeriod },
            {
                range: CompareRangeType.weekLastYearTantamount,
                default: ComparisonType.previousYear,
            },
            { range: CompareRangeType.forceSelection },
        ],
    },
    [RangeType.onGoingWeek]: {
        [ComparisonMode.dateToDate]: [
            { range: CompareRangeType.lastWeek, default: ComparisonType.previousPeriod },
            { range: CompareRangeType.lastFourWeeks, default: ComparisonType.meanOfPeriod },
            {
                range: CompareRangeType.weekLastYearDateToDate,
                default: ComparisonType.previousYear,
            },
            { range: CompareRangeType.forceSelection },
        ],
        [ComparisonMode.tantamount]: [
            { range: CompareRangeType.lastWeek, default: ComparisonType.previousPeriod },
            { range: CompareRangeType.lastFourWeeks, default: ComparisonType.meanOfPeriod },
            {
                range: CompareRangeType.weekLastYearTantamount,
                default: ComparisonType.previousYear,
            },
            { range: CompareRangeType.forceSelection },
        ],
    },
    [RangeType.currentWeek]: {
        [ComparisonMode.dateToDate]: [
            { range: CompareRangeType.lastWeek, default: ComparisonType.previousPeriod },
            { range: CompareRangeType.lastFourWeeks, default: ComparisonType.meanOfPeriod },
            {
                range: CompareRangeType.weekLastYearDateToDate,
                default: ComparisonType.previousYear,
            },
            { range: CompareRangeType.forceSelection },
        ],
        [ComparisonMode.tantamount]: [
            { range: CompareRangeType.lastWeek, default: ComparisonType.previousPeriod },
            { range: CompareRangeType.lastFourWeeks, default: ComparisonType.meanOfPeriod },
            {
                range: CompareRangeType.weekLastYearTantamount,
                default: ComparisonType.previousYear,
            },
            { range: CompareRangeType.forceSelection },
        ],
    },
    [RangeType.yesterday]: {
        [ComparisonMode.dateToDate]: [
            { range: CompareRangeType.previousWeekSameDay, default: ComparisonType.previousPeriod },
            { range: CompareRangeType.lastFourWeeksSameDay, default: ComparisonType.meanOfPeriod },
            { range: CompareRangeType.dayLastYearDateToDate, default: ComparisonType.previousYear },
            { range: CompareRangeType.forceSelection },
        ],
        [ComparisonMode.tantamount]: [
            { range: CompareRangeType.previousWeekSameDay, default: ComparisonType.previousPeriod },
            { range: CompareRangeType.lastFourWeeksSameDay, default: ComparisonType.meanOfPeriod },
            { range: CompareRangeType.dayLastYearTantamount, default: ComparisonType.previousYear },
            { range: CompareRangeType.forceSelection },
        ],
    },
    [RangeType.currentDay]: {
        [ComparisonMode.dateToDate]: [
            { range: CompareRangeType.previousWeekSameDay, default: ComparisonType.previousPeriod },
            { range: CompareRangeType.lastFourWeeksSameDay, default: ComparisonType.meanOfPeriod },
            { range: CompareRangeType.dayLastYearDateToDate, default: ComparisonType.previousYear },
            { range: CompareRangeType.forceSelection },
        ],
        [ComparisonMode.tantamount]: [
            { range: CompareRangeType.previousWeekSameDay, default: ComparisonType.previousPeriod },
            { range: CompareRangeType.lastFourWeeksSameDay, default: ComparisonType.meanOfPeriod },
            { range: CompareRangeType.dayLastYearTantamount, default: ComparisonType.previousYear },
            { range: CompareRangeType.forceSelection },
        ],
    },
    [RangeType.today]: {
        [ComparisonMode.dateToDate]: [
            { range: CompareRangeType.previousWeekSameDay, default: ComparisonType.previousPeriod },
            { range: CompareRangeType.lastFourWeeksSameDay, default: ComparisonType.meanOfPeriod },
            { range: CompareRangeType.dayLastYearDateToDate, default: ComparisonType.previousYear },
            { range: CompareRangeType.forceSelection },
        ],
        [ComparisonMode.tantamount]: [
            { range: CompareRangeType.previousWeekSameDay, default: ComparisonType.previousPeriod },
            { range: CompareRangeType.lastFourWeeksSameDay, default: ComparisonType.meanOfPeriod },
            { range: CompareRangeType.dayLastYearTantamount, default: ComparisonType.previousYear },
            { range: CompareRangeType.forceSelection },
        ],
    },
    [RangeType.selection]: {
        [ComparisonMode.dateToDate]: [
            { range: CompareRangeType.selectionDateToDate, default: ComparisonType.previousPeriod },
        ],
        [ComparisonMode.tantamount]: [
            { range: CompareRangeType.selectionTantamount, default: ComparisonType.previousPeriod },
        ],
    },
};
